import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
  canAccessModule: (moduleId: number) => boolean;
  isAdmin: boolean;
  userRole: 'parent' | 'child' | null;
  isAdminChild: boolean;
  hasPaidChildren: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { hasActiveSubscription, isLoading: subscriptionLoading, refetch, isInherited } = useSubscription();
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<'parent' | 'child' | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [hasPaidChildren, setHasPaidChildren] = useState(false);
  
  // Admin child status comes from subscription hook
  const isAdminChild = isInherited;

  // Check user profile (admin status and role)
  useEffect(() => {
    const checkUserProfile = async () => {
      if (!user) {
        setIsAdmin(false);
        setUserRole(null);
        setHasPaidChildren(false);
        setProfileLoading(false);
        return;
      }

      try {
        // Check user role from profiles table
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        let resolvedRole: 'parent' | 'child' | null = null;
        if (profileError) {
          console.error('Error checking user profile:', profileError);
          setUserRole(null);
        } else {
          resolvedRole = profile?.role || null;
          setUserRole(resolvedRole);
        }

        // Check admin status from user_roles table (secure)
        const { data: roles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin');

        if (rolesError) {
          console.error('❌ Error checking user roles:', rolesError);
          setIsAdmin(false);
        } else {
          const hasAdmin = roles && roles.length > 0;
          console.log('🔐 Admin check result:', { userId: user.id, roles, hasAdmin });
          setIsAdmin(hasAdmin);
        }

        // If parent, check whether any of their children has an active subscription they paid for
        if (resolvedRole === 'parent') {
          const { data: childSubs, error: childSubsError } = await supabase
            .from('user_subscriptions')
            .select('id')
            .eq('parent_payer_id', user.id)
            .eq('is_active', true)
            .gt('expires_at', new Date().toISOString())
            .limit(1);

          if (childSubsError) {
            console.error('Error checking paid children:', childSubsError);
            setHasPaidChildren(false);
          } else {
            setHasPaidChildren((childSubs?.length ?? 0) > 0);
          }
        } else {
          setHasPaidChildren(false);
        }
      } catch (error) {
        console.error('Error in checkUserProfile:', error);
        setIsAdmin(false);
        setUserRole(null);
        setHasPaidChildren(false);
      } finally {
        setProfileLoading(false);
      }
    };

    checkUserProfile();
  }, [user]);

  const isLoading = subscriptionLoading || profileLoading;

  const canAccessModule = (moduleId: number): boolean => {
    // Admins can access all modules
    if (isAdmin) return true;
    
    // Admin children get free access to all modules
    if (isAdminChild) {
      console.log('🔓 [SubscriptionContext] Admin child accessing module', moduleId);
      return true;
    }
    
    // Module 1 is always free (demo module)
    if (moduleId === 1) return true;
    
    // Parents can only access module 1 (demo) - they should use the dashboard to monitor children
    if (userRole === 'parent') return false;
    
    // Children can access other modules if they have their own active subscription
    if (userRole === 'child') return hasActiveSubscription;
    
    // Default fallback for users without a defined role
    return hasActiveSubscription;
  };

  const refreshSubscription = async () => {
    if (user) {
      await refetch();
    }
  };

  const value: SubscriptionContextType = {
    hasActiveSubscription,
    isLoading,
    refreshSubscription,
    canAccessModule,
    isAdmin,
    userRole,
    isAdminChild,
    hasPaidChildren,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};